import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import type { LoadOptionsResponse } from '@shared/components/ui/MultiSelect';
import { ClientsApiAdapter } from '@modules/Clients/List/adapters/ClientsApiAdapter';
import { ProductApiAdapter } from '@shared/adapters/ProductApiAdapter';
import { ProductService } from '@shared/services/ProductService';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';

/**
 * Estado interno de los filtros de Entries
 * Nota: El filtro de status se maneja mediante tabs (EntryStatusToolbar)
 */
interface EntriesFiltersState {
  createdAt: DateRange | undefined;
  estimatedDeliveryAt: DateRange | undefined;
  reference: string;
  trackingCode: string;
  client: string[];
  products: string[];
}

interface UseEntriesFiltersProps {
  /** Condiciones iniciales desde query params */
  initialConditions?: FilterConditionOutput[];
  /** Callback cuando cambian los filtros */
  onChange?: (conditions: FilterConditionOutput[]) => void;
  /** Si mostrar el filtro de cliente (solo para tenant_3pl) */
  hasTeamData?: boolean;
}

/**
 * Hook para gestionar los filtros de Entries
 *
 * Convierte el estado de los filtros UI al formato FilterConditionOutput[]
 * esperado por la API de advanced_filters.
 *
 * Nota: El filtro de status se maneja mediante tabs (EntryStatusToolbar)
 */
export const useEntriesFilters = ({ initialConditions = [], onChange }: UseEntriesFiltersProps) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const clientsAdapter = useMemo(() => new ClientsApiAdapter(httpClient), [httpClient]);
  const productService = useMemo(() => new ProductService(new ProductApiAdapter(httpClient)), [httpClient]);

  /** Búsqueda remota + paginación (scroll infinito) para el MultiSelect de clientes */
  const loadClientOptions = useCallback(
    async ({ search, page, limit }: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      const q = search.trim();
      const pageData = await clientsAdapter.getClientsPaginated({
        page,
        limit,
        sortBy: 'name',
        sortDirection: 'asc',
        ...(q.length >= 2 ? { name: q } : {}),
      });
      return {
        options: pageData.clients.map((c) => ({
          label: c.name,
          value: String(c.id),
        })),
        hasMore: pageData.hasMore,
      };
    },
    [clientsAdapter],
  );

  const loadProductsForMultiSelect = useCallback(
    async (p: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      try {
        const result = await productService.getProductsPaginated({
          searchTerm: p.search,
          page: p.page,
          limit: p.limit,
        });
        return {
          options: result.products.map((product) => ({
            value: product.id.toString(),
            label: product.name || `Producto ${product.id}`,
            description: product.code || undefined,
          })),
          hasMore: result.hasMore,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [productService],
  );

  // Estado de los filtros
  const [filters, setFilters] = useState<EntriesFiltersState>(() => {
    return parseConditionsToState(initialConditions);
  });

  // Convertir estado a FilterConditionOutput[]
  const convertStateToConditions = useCallback((state: EntriesFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    // Fecha de creación (created_at)
    if (state.createdAt?.from && state.createdAt?.to) {
      conditions.push({
        column: 'created_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.createdAt.from),
      });
      conditions.push({
        column: 'created_at',
        action: 'less_equal',
        value: formatDateForAPI(state.createdAt.to),
      });
    }

    // Fecha estimada de entrega (estimated_delivery_at)
    if (state.estimatedDeliveryAt?.from && state.estimatedDeliveryAt?.to) {
      conditions.push({
        column: 'estimated_delivery_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.estimatedDeliveryAt.from),
      });
      conditions.push({
        column: 'estimated_delivery_at',
        action: 'less_equal',
        value: formatDateForAPI(state.estimatedDeliveryAt.to),
      });
    }

    // Referencia (reference) - búsqueda parcial
    if (state.reference && state.reference.trim()) {
      conditions.push({
        column: 'reference',
        action: 'contains',
        value: state.reference.trim(),
      });
    }

    // Código de tracking (tracking_code) - búsqueda parcial
    if (state.trackingCode && state.trackingCode.trim()) {
      conditions.push({
        column: 'tracking_code',
        action: 'contains',
        value: state.trackingCode.trim(),
      });
    }

    // Cliente (team_id) - filtro múltiple con operador 'in'
    if (state.client.length > 0) {
      conditions.push({
        column: 'team_id',
        action: 'in',
        value: state.client,
      });
    }

    // Productos (product_ids) - filtro múltiple con operador 'in'
    if (state.products.length > 0) {
      conditions.push({
        column: 'product_ids',
        action: 'in',
        value: state.products,
      });
    }

    return conditions;
  }, []);

  // Actualizar filtros y notificar cambio
  const updateFilters = useCallback(
    (updates: Partial<EntriesFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        const conditions = convertStateToConditions(newState);
        onChange?.(conditions);
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  // Handlers individuales
  const handleCreatedAtChange = useCallback(
    (range: DateRange | undefined) => {
      updateFilters({ createdAt: range });
    },
    [updateFilters],
  );

  const handleEstimatedDeliveryAtChange = useCallback(
    (range: DateRange | undefined) => {
      updateFilters({ estimatedDeliveryAt: range });
    },
    [updateFilters],
  );

  const handleReferenceChange = useCallback(
    (value: string) => {
      updateFilters({ reference: value });
    },
    [updateFilters],
  );

  const handleTrackingCodeChange = useCallback(
    (value: string) => {
      updateFilters({ trackingCode: value });
    },
    [updateFilters],
  );

  const handleClientChange = useCallback(
    (value: string[]) => {
      updateFilters({ client: value });
    },
    [updateFilters],
  );

  const handleProductsChange = useCallback(
    (value: string[]) => {
      updateFilters({ products: value });
    },
    [updateFilters],
  );

  // Limpiar todos los filtros
  const clearAllFilters = useCallback(() => {
    const emptyState: EntriesFiltersState = {
      createdAt: undefined,
      estimatedDeliveryAt: undefined,
      reference: '',
      trackingCode: '',
      client: [],
      products: [],
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  // Sincronizar con condiciones iniciales cuando cambien
  useEffect(() => {
    const newState = parseConditionsToState(initialConditions);
    setFilters(newState);
  }, [initialConditions]);

  // Obtener condiciones actuales
  const currentConditions = useMemo(() => convertStateToConditions(filters), [filters, convertStateToConditions]);

  return {
    // Estado de filtros
    filters,

    // Handlers
    handleCreatedAtChange,
    handleEstimatedDeliveryAtChange,
    handleReferenceChange,
    handleTrackingCodeChange,
    handleClientChange,
    handleProductsChange,
    clearAllFilters,

    loadClientOptions,
    loadProductsForMultiSelect,

    // Condiciones actuales en formato API
    currentConditions,
  };
};

/**
 * Parsea condiciones FilterConditionOutput[] al estado de filtros
 * Nota: Ignora las condiciones de status ya que se manejan via tabs
 */
function parseConditionsToState(conditions: FilterConditionOutput[]): EntriesFiltersState {
  const state: EntriesFiltersState = {
    createdAt: undefined,
    estimatedDeliveryAt: undefined,
    reference: '',
    trackingCode: '',
    client: [],
    products: [],
  };

  // Agrupar condiciones por columna
  const byColumn: Record<string, FilterConditionOutput[]> = {};
  conditions.forEach((condition) => {
    if (!byColumn[condition.column]) {
      byColumn[condition.column] = [];
    }
    byColumn[condition.column].push(condition);
  });

  // Helper para parsear rangos de fechas
  const parseDateRange = (column: string): DateRange | undefined => {
    const dateConditions = byColumn[column] || [];
    const dateFrom = dateConditions.find((c) => c.action === 'greater_equal');
    const dateTo = dateConditions.find((c) => c.action === 'less_equal');
    if (dateFrom?.value && dateTo?.value) {
      return {
        from: new Date(dateFrom.value),
        to: new Date(dateTo.value),
      };
    }
    return undefined;
  };

  // Fechas
  state.createdAt = parseDateRange('created_at');
  state.estimatedDeliveryAt = parseDateRange('estimated_delivery_at');

  // Strings con operador 'contains'
  const referenceCondition = byColumn['reference']?.find((c) => c.action === 'contains');
  state.reference = referenceCondition?.value || '';

  const trackingCodeCondition = byColumn['tracking_code']?.find((c) => c.action === 'contains');
  state.trackingCode = trackingCodeCondition?.value || '';

  // Cliente (team_id) con operador 'in'
  const clientCondition = byColumn['team_id']?.find((c) => c.action === 'in');
  if (clientCondition?.value) {
    state.client = Array.isArray(clientCondition.value) ? clientCondition.value : [clientCondition.value];
  }

  // Productos (product_ids) con operador 'in'
  const productCondition =
    byColumn['product_ids']?.find((c) => c.action === 'in') ?? byColumn['product_id']?.find((c) => c.action === 'in');
  if (productCondition?.value) {
    state.products = Array.isArray(productCondition.value) ? productCondition.value : [productCondition.value];
  }

  return state;
}

/**
 * Formatea una fecha para la API (formato YYYY-MM-DD)
 * Usa métodos locales para evitar problemas de timezone
 */
function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
